import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  getInstitutes,
  getBranches,
  getBatches,
  getAdminList,
  getBranchList,
  getAvailableCourses,
  getWebexUsers,
  saveBranch,
  editBranch,
  deleteBranch,
  getBranchById,
  getInstituteList,
  getAdditionalCourses,
  getInstituteById,
  getCourses,
  addInstitute,
  editInstitute,
  getCoachingCenter
} from './api'
import {
  Institute,
  InitialState,
  Branch,
  GetAdminResponse,
  Batch,
  Admin,
  GetBranchListResponse,
  Course,
  WebexUser,
  GetInstituteListResponse,
  AdditionalCourse
} from './types'

const initialState: InitialState = {
  isLoading: false,
  institutes: [],
  branches: [],
  batches: [],
  courses: [],
  additionalCourses: [],
  admin: null,
  instituteList: null,
  branchList: null,
  availableCourses: [],
  webexUsers: [],
  selectedInstitute: {
    address1: '',
    address2: '',
    city: '',
    coachingCenterCode: '',
    coachingCentreName: '',
    country: '',
    state: '',
    email: '',
    expiryOn: '',
    id: '',
    mobileNumber: '',
    questionLimit: '',
    status: '',
    zipCode: '',
    teacherCount: '',
    studentCount: ''
  },
  selectedBranch: {
    address1: '',
    address2: '',
    branchName: '',
    city: '',
    coachingCenterId: '',
    country: '',
    courseIds: [],
    email: '',
    id: '',
    isMainBranch: '',
    mobileNumber: '',
    questionLimit: '',
    state: '',
    status: '',
    webexUserIds: [],
    webexUsers: [],
    zipCode: ''
  },
  selectedAdmin: {
    id: '',
    userType: '',
    user: { id: '', userName: '' },
    firstName: '',
    dob: '',
    lastName: '',
    mobileNumber: '',
    fatherName: '',
    status: '',
    email: ''
  }
}

export const userSlice = createSlice({
  name: 'coachingCenter',
  initialState,
  reducers: {
    selectAdmin: (state, action: PayloadAction<Admin | null>) => {
      state.selectedAdmin = action?.payload
    },
    resetBranch: (state) => {
      state.selectedBranch = null
      state.availableCourses = []
    },
    resetInstitutes: (state) => {
      state.institutes = []
    },
    resetInstitute: (state) => {
      state.selectedInstitute = null
      state.additionalCourses = []
    },
    resetAvailableCourse: (state) => {
      state.availableCourses = []
    }
  },
  extraReducers: {
    [getCoachingCenter.pending.toString()]: (state) => {
      state.institutes = []
      state.isLoading = true
    },
    [getCoachingCenter.fulfilled.toString()]: (
      state,
      action: PayloadAction<Array<Institute>>
    ) => {
      state.institutes = action?.payload
      state.isLoading = false
    },
    [getCoachingCenter.rejected.toString()]: (state) => {
      state.isLoading = true
    },
    [getInstitutes.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getInstitutes.fulfilled.toString()]: (
      state,
      action: PayloadAction<Array<Institute>>
    ) => {
      state.isLoading = false
      state.institutes = action?.payload
      state.branches = []
      state.batches = []
    },
    [getInstitutes.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [getBranches.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getBranches.fulfilled.toString()]: (
      state,
      action: PayloadAction<Array<Branch>>
    ) => {
      state.isLoading = false
      state.branches = action?.payload
    },
    [getBranches.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [getBatches.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getBatches.fulfilled.toString()]: (
      state,
      action: PayloadAction<Array<Batch>>
    ) => {
      state.isLoading = false
      state.batches = action?.payload
    },
    [getBatches.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [getInstituteList.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getInstituteList.fulfilled.toString()]: (
      state,
      action: PayloadAction<GetInstituteListResponse>
    ) => {
      state.isLoading = false
      state.instituteList = action?.payload
    },
    [getInstituteList.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [getAdditionalCourses.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getAdditionalCourses.fulfilled.toString()]: (
      state,
      action: PayloadAction<Array<AdditionalCourse>>
    ) => {
      state.isLoading = false
      state.additionalCourses = action?.payload
    },
    [getAdditionalCourses.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [getBranchList.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getBranchList.fulfilled.toString()]: (
      state,
      action: PayloadAction<GetBranchListResponse>
    ) => {
      state.isLoading = false
      state.branchList = action?.payload
    },
    [getBranchList.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [getAdminList.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getAdminList.fulfilled.toString()]: (
      state,
      action: PayloadAction<GetAdminResponse>
    ) => {
      state.isLoading = false
      state.admin = action?.payload
    },
    [getAdminList.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [getAvailableCourses.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getAvailableCourses.fulfilled.toString()]: (
      state,
      action: PayloadAction<Array<Course>>
    ) => {
      state.isLoading = false
      state.availableCourses = action?.payload
    },
    [getAvailableCourses.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [getWebexUsers.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getWebexUsers.fulfilled.toString()]: (
      state,
      action: PayloadAction<Array<WebexUser>>
    ) => {
      state.isLoading = false
      state.webexUsers = action?.payload
    },
    [getWebexUsers.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [saveBranch.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [saveBranch.fulfilled.toString()]: (state) => {
      state.isLoading = false
    },
    [saveBranch.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [editBranch.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [editBranch.fulfilled.toString()]: (state) => {
      state.isLoading = false
    },
    [editBranch.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [deleteBranch.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [deleteBranch.fulfilled.toString()]: (state) => {
      state.isLoading = false
    },
    [deleteBranch.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [getBranchById.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getBranchById.fulfilled.toString()]: (
      state,
      action: PayloadAction<Branch | null>
    ) => {
      state.selectedBranch = action?.payload
    },
    [getBranchById.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [getInstituteById.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getInstituteById.fulfilled.toString()]: (
      state,
      action: PayloadAction<Institute | null>
    ) => {
      state.isLoading = false
      state.selectedInstitute = action?.payload
    },
    [getInstituteById.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [getCourses.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [getCourses.fulfilled.toString()]: (
      state,
      action: PayloadAction<Array<Course>>
    ) => {
      state.isLoading = false
      state.courses = action?.payload
    },
    [getCourses.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [addInstitute.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [addInstitute.fulfilled.toString()]: (state) => {
      state.isLoading = false
    },
    [addInstitute.rejected.toString()]: (state) => {
      state.isLoading = false
    },
    [editInstitute.pending.toString()]: (state) => {
      state.isLoading = true
    },
    [editInstitute.fulfilled.toString()]: (state) => {
      state.isLoading = false
    },
    [editInstitute.rejected.toString()]: (state) => {
      state.isLoading = false
    }
  }
})

export default userSlice.reducer
